import {NgModule} from '@angular/core';
import {CoreModule} from '../../../../../core/core.module';

import {CwCustomerListsComponent} from './cw-customer-lists.component';

/**
 * The cw sub-module that carries the customer-scoped Vorgangsliste into the cr customer page —
 * TM-1000002000: one list („Laufende Anträge") since the Antrag section was removed; the module and
 * its single import are unchanged by that, CoreModule still covers everything the component needs —
 * modelled on {@code CwRequestModule}, and built for exactly one purpose: to be importable by
 * {@code CrModule} WITHOUT closing a module cycle.
 *
 * <h3>The one rule this module lives by</h3>
 * The edge that matters is BOUND-002 — no cycle. {@code CwModule} imports {@code CrModule}
 * ({@code cw.module.ts:226/473}), so {@code CrModule} may import anything cw that does NOT reach
 * back to {@code CrModule}. This module reaches only {@code CoreModule}, and the whole point of it
 * is that it stays that way. {@code CwMyRequestsComponent} could not be reused for the same reason:
 * it is declared by {@code CwModule}, a class may be declared in only ONE NgModule, and importing
 * {@code CwModule} into {@code CrModule} IS the cycle.
 *
 * <p>Measured before this module was written (BFS over the {@code imports} arrays of every
 * {@code *.module.ts} in the workspace, with {@code CwModule → CrModule} as the positive control):
 * from {@code CoreModule}, 109 NgModules are reachable and {@code CrModule} is not among them;
 * neither are the three modules that DO import it ({@code CiModule}, {@code CwModule},
 * {@code ApModule}). The TypeScript side was measured the same way: the import closure of the two
 * widget builders and the document counter is 165–167 files and contains not a single {@code cr}
 * file, so no ES-module cycle can arise either. (TM-1000002000: measured when the component still
 * carried the Antrag section; removing the request builder and the counter only SHRANK that closure,
 * and the added navigation util imports only {@code @angular/router}, core and cw files.)
 *
 * @ticket TM-1000002000
 */
@NgModule({
    imports: [
        // CoreModule carries SharedModule — mvs-widget, the custom-field directive and the PrimeNG
        // surface. That is everything this module's one component needs.
        //
        // NEVER ADD CrModule HERE. Not for cr-object-addresses, not for a cr DTO, not "just for a
        // type". CrModule imports THIS module, so a cr import in this array closes the cr ↔ cw
        // cycle — and an NgModule cycle does not fail to compile, it fails at RUNTIME, when one of
        // the two module classes is still `undefined` while the other is being evaluated. CwModule
        // states the same direction at cw.module.ts:224-226; this module is the mirror of it and
        // only stays valid while this array stays free of cr.
        //
        // The same applies to anything that REACHES CrModule: CwModule itself, CiModule and
        // ApModule are the three modules in this workspace that import it (measured).
        CoreModule,
    ],
    declarations: [
        CwCustomerListsComponent,
    ],
    exports: [
        // The export is what makes <cw-customer-lists> resolvable inside the cr template. The cr
        // component's TypeScript never references this class — a template selector creates no
        // import, which is exactly how dm and tm components already reach that same page.
        CwCustomerListsComponent,
    ],
    // NO ENTITY_SERVICE_REGISTRY PROVIDER HERE, AND THAT IS A MEASURED DECISION — NOT AN OVERSIGHT.
    //
    // The pattern this module copies (CwRequestModule, cw-request.entity-provider.ts:59-66) carries
    // its own registry entries, because a shell that has the components but not the aliases renders
    // metadata-driven forms and ENTITY-datasource widgets SILENTLY EMPTY. Two measurements say that
    // failure mode cannot reach this module, and that adding entries anyway would be harmful:
    //
    //  1. The list here (TM-1000002000: one since the Antrag section was removed) is a
    //     `createWidgetTableQl` widget, and that factory sets
    //     `dataProviderObject` to the EMPTY STRING (widget.factory.ts, createWidgetTableQl ->
    //     createWidgetList). The QL load path therefore resolves no entity alias at all; the entity
    //     names live inside the QlQueryDto. There is no alias for this module to be short of.
    //  2. EntityServiceCollector folds every contribution with `map.set(key, cfg)` — LAST ONE WINS
    //     (entity-service-collector.service.ts:13-17). A leaner duplicate of `cw.CwCase` would
    //     therefore be able to REPLACE the canonical entry from CW_ENTITY_PROVIDER, which carries
    //     `service`, `objectComponent` and `objectPageComponent` (cw.entity-provider.ts:227). This
    //     module cannot reproduce the last two — they point at CwCaseObjectComponent, declared by
    //     CwModule — so any entry it wrote would be POORER, and whether it wins depends on module
    //     visit order. Registering it would trade a failure mode that cannot occur here for one
    //     that can.
    //
    // If a future component in this module renders an ENTITY-datasource widget or a metadata-driven
    // form, the calculus flips and the entries become mandatory. Add them then — and add them by
    // REUSING the existing entry objects (CW_REQUEST_ENTITY_ENTRIES exports its own), never by
    // retyping a shorter config.
})
export class CwCustomerListsModule {
}
